/**
 * The week model on the client (Phase 6): how Home and the Sheets page lay sheets out by
 * week, sort the table, word a budget, and which status changes a user may make. Pure,
 * so the rules can be tested without a browser (tests/unit/weeks.test.ts).
 */
import type { SheetKind, SheetStatus, TermSlot, TermView } from '@studio/shared';
import { DEFAULT_TERM_WEEKS, sheetLabelForWeek, weekLabel } from '@studio/shared';
import { UNDER_SHARE, formatMinutes } from './budget';
import { getLocale, localized } from './i18n';

/** `2026/27` — how a term is written. */
export const termName = (t: string): string => `${t}/${String(Number(t) + 1).slice(2)}`;

/* ---------------------------------------------------------------- views */

export const SHEETS_VIEWS = ['list', 'weeks', 'table', 'bin'] as const;
export type SheetsView = (typeof SHEETS_VIEWS)[number];

/** The Sheets page's view from `?view=`; anything else is the list. */
export function viewFromParam(value: string | null): SheetsView {
  return (SHEETS_VIEWS as readonly string[]).includes(value ?? '') ? (value as SheetsView) : 'list';
}

/* --------------------------------------------------------- the weeks grid */

/** The Weeks grid's columns: the two weekly series, and everything else together. */
export const WEEK_COLUMNS = localized('week columns', {
  en: [
    { key: 'tutorial', label: 'Tutorial', kind: 'tutorial' },
    { key: 'exercise', label: 'Exercise sheet', kind: 'exercise' },
    { key: 'other', label: 'Other', kind: 'other' },
  ] as const satisfies readonly { key: string; label: string; kind: SheetKind }[],
  de: [
    { key: 'tutorial', label: 'Tutorium', kind: 'tutorial' },
    { key: 'exercise', label: 'Übungsblatt', kind: 'exercise' },
    { key: 'other', label: 'Sonstiges', kind: 'other' },
  ],
});
export type WeekColumn = (typeof WEEK_COLUMNS)[number]['key'];

export const columnOf = (kind: string): WeekColumn =>
  kind === 'tutorial' ? 'tutorial' : kind === 'exercise' ? 'exercise' : 'other';

/** One row of the Weeks grid: a teaching week, a break, or the sheets without a week. */
export interface WeekRow {
  key: string;
  /** null: a break (skipped) or the "no week set" row */
  week: number | null;
  skipped: boolean;
  /** first and last day, when the term has a calendar */
  from?: string;
  to?: string;
  holidays: string[];
}

interface Weekly {
  week?: number | null;
}

/**
 * The rows of the Weeks grid, in calendar order: every slot of the term's calendar —
 * breaks included, so the Christmas gap is visible where it falls — then any week a
 * sheet names beyond the calendar, then "no week set" when a sheet has none. Without a
 * calendar (a term nobody configured) the weeks are numbered, undated, W0 … W14 or as
 * far as the sheets go.
 */
export function weekRows(cal: Pick<TermView, 'slots'> | null, sheets: Weekly[]): WeekRow[] {
  const rows: WeekRow[] = [];
  const weeks = sheets.map((s) => s.week).filter((w): w is number => typeof w === 'number');
  let last = -1;
  if (cal && cal.slots.length) {
    for (const s of cal.slots) {
      rows.push(slotRow(s));
      if (s.week !== null) last = Math.max(last, s.week);
    }
  } else {
    for (let w = 0; w < DEFAULT_TERM_WEEKS; w++) rows.push({ key: `w${w}`, week: w, skipped: false, holidays: [] });
    last = DEFAULT_TERM_WEEKS - 1;
  }
  const beyond = [...new Set(weeks.filter((w) => w > last))].sort((a, b) => a - b);
  for (const w of beyond) rows.push({ key: `w${w}`, week: w, skipped: false, holidays: [] });
  if (sheets.some((s) => typeof s.week !== 'number')) rows.push({ key: 'none', week: null, skipped: false, holidays: [] });
  return rows;
}

const slotRow = (s: TermSlot): WeekRow => ({
  key: s.week === null ? `slot${s.slot}` : `w${s.week}`,
  week: s.week,
  skipped: s.skipped,
  from: s.from,
  to: s.to,
  holidays: s.holidays,
});

/** Sheets per teaching week, for the chips' counts. */
export function countsByWeek(sheets: Weekly[]): Record<number, number> {
  const out: Record<number, number> = {};
  for (const s of sheets) if (typeof s.week === 'number') out[s.week] = (out[s.week] ?? 0) + 1;
  return out;
}

/**
 * `20 Oct – 26 Oct` (or `29 Dec – 4 Jan` across a year), from `YYYY-MM-DD` dates; in
 * German `20. Okt – 26. Okt`.
 */
export function dateRange(from: string, to: string): string {
  const de = getLocale() === 'de';
  const fmt = (d: string) => {
    const [, m, day] = d.split('-').map(Number);
    return de ? `${day}. ${MONTHS_DE[(m ?? 1) - 1]}` : `${day} ${MONTHS[(m ?? 1) - 1]}`;
  };
  return `${fmt(from)} – ${fmt(to)}`;
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** What a week is called with its dates: `W3 · 10 Nov – 16 Nov`. */
export function weekTitle(week: number, cal: Pick<TermView, 'slots'> | null): string {
  const slot = cal?.slots.find((s) => s.week === week);
  return slot ? `${weekLabel(week)} · ${dateRange(slot.from, slot.to)}` : weekLabel(week);
}

/* ------------------------------------------------------------ the past */

interface PastSheet {
  id: string;
  year: string;
  label: string;
  kind: string;
}

/**
 * Last year's sheet of this week: the newest past sheet of the series whose number is
 * the week's (week 0 is sheet 0, week 1 sheet A …). Null when there is none.
 */
export function pastSheetForWeek<T extends PastSheet>(list: T[], kind: string, week: number): T | null {
  const label = sheetLabelForWeek(week);
  if (!label) return null;
  return (
    list
      .filter((s) => s.kind === kind && s.label.trim().toUpperCase() === label)
      .sort((a, b) => b.year.localeCompare(a.year))[0] ?? null
  );
}

/* ---------------------------------------------------------------- budget */

/** the figures a summary carries */
export interface BudgetFigures {
  minutes?: number;
  unknownMinutes?: number;
  targetMinutes?: number | null;
  exerciseCount?: number;
  points?: number;
}

/** Same rule as the editor's budget strip: over, well under (below UNDER_SHARE), fine, or no target. */
export function budgetState(s: BudgetFigures): 'none' | 'under' | 'ok' | 'over' {
  const target = s.targetMinutes;
  if (typeof target !== 'number' || !(target > 0)) return 'none';
  const minutes = s.minutes ?? 0;
  return minutes > target ? 'over' : minutes < target * UNDER_SHARE ? 'under' : 'ok';
}

/**
 * The budget in a few characters: `75 min / 1 h 30 min`, `75 min` without a target, `—` with no
 * exercises. `+2?` says that many exercises have no estimate, so the total is a floor.
 */
/**
 * A sheet's points in a few characters: "—" when none of its exercises has points (every
 * tutorial), so that does not read as "worth nothing"; "12 +3?" when some lack them.
 */
export function pointsText(s: { points?: number; unknownPoints?: number }): string {
  const points = s.points ?? 0;
  const unknown = s.unknownPoints ?? 0;
  if (!points && unknown) return '—';
  return unknown ? `${points} +${unknown}?` : String(points);
}

export function budgetText(s: BudgetFigures): string {
  if (!s.exerciseCount) return s.targetMinutes ? `0 min / ${formatMinutes(s.targetMinutes)}` : '—';
  const minutes = s.minutes ?? 0;
  const unknown = s.unknownMinutes ? ` +${s.unknownMinutes}?` : '';
  if (typeof s.targetMinutes === 'number' && s.targetMinutes > 0) {
    return `${formatMinutes(minutes)}${unknown} / ${formatMinutes(s.targetMinutes)}`;
  }
  return `${formatMinutes(minutes)}${unknown}`;
}

/* ----------------------------------------------------------------- status */

export const STATUS_ORDER: readonly SheetStatus[] = ['draft', 'ready', 'published'];

/**
 * The statuses a user may pick from `current`. Draft and Ready are anyone's who can
 * edit; Published is set by the owner, an admin, or a merged GitLab review. Once a sheet
 * is published, someone who may not publish can only take it back to Draft.
 */
export function allowedStatuses(current: SheetStatus, canPublish: boolean): SheetStatus[] {
  if (canPublish) return [...STATUS_ORDER];
  if (current === 'published') return ['draft', 'published'];
  return ['draft', 'ready'];
}

/** Leaving Published needs a confirmation: it says the handed-out version is being changed. */
export const statusChangeNeedsConfirm = (from: SheetStatus, to: SheetStatus): boolean =>
  from === 'published' && to !== 'published';

/* ---------------------------------------------------------------- table */

export const SORT_KEYS = ['title', 'kind', 'week', 'status', 'minutes', 'points', 'updated', 'owner'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

interface Sortable {
  title: string;
  label: string;
  kind: string;
  week?: number | null;
  status?: SheetStatus;
  minutes?: number;
  points?: number;
  updatedAt: string;
  ownerName: string;
}

/**
 * The table's order. Missing values (no week, no estimate) sort last in both directions:
 * "the sheets without a week" are not the smallest weeks.
 */
export function sortSheets<T extends Sortable>(
  list: T[],
  key: SortKey,
  dir: 'asc' | 'desc',
  titleOf: (s: T) => string = (s) => s.title || s.label,
): T[] {
  const value = (s: T): string | number | null => {
    switch (key) {
      case 'title':
        return titleOf(s).toLowerCase();
      case 'kind':
        return s.kind;
      case 'week':
        return typeof s.week === 'number' ? s.week : null;
      case 'status':
        return STATUS_ORDER.indexOf(s.status ?? 'draft');
      case 'minutes':
        return typeof s.minutes === 'number' ? s.minutes : null;
      case 'points':
        return typeof s.points === 'number' ? s.points : null;
      case 'updated':
        return s.updatedAt;
      case 'owner':
        return s.ownerName.toLowerCase();
    }
  };
  const sign = dir === 'asc' ? 1 : -1;
  return list
    .map((s, i) => ({ s, i, v: value(s) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) return a.v === b.v ? a.i - b.i : a.v === null ? 1 : -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : String(a.v).localeCompare(String(b.v));
      return c ? c * sign : a.i - b.i;
    })
    .map((x) => x.s);
}

/* -------------------------------------------------------------- continue */

const LAST_SHEET_KEY = 'studio.lastSheet';

/** Called by the editor when a sheet opens, for Home's "Continue". */
export function rememberLastSheet(id: string): void {
  try {
    localStorage.setItem(LAST_SHEET_KEY, id);
  } catch {
    /* private mode: Home falls back to the server's last edit */
  }
}

export function lastSheetId(): string | null {
  try {
    return localStorage.getItem(LAST_SHEET_KEY);
  } catch {
    return null;
  }
}

/**
 * The sheet Home offers to continue: the one last opened in this browser while it still
 * exists, else the most recently changed sheet of the user's (owned or shared with
 * them), else nothing.
 */
export function pickContinue<T extends { id: string; ownerId: number; updatedAt: string; collaborators?: { id: number }[] }>(
  sheets: T[],
  lastId: string | null,
  userId: number | undefined,
): T | null {
  if (lastId) {
    const hit = sheets.find((s) => s.id === lastId);
    if (hit) return hit;
  }
  const mine = sheets.filter((s) => s.ownerId === userId || s.collaborators?.some((c) => c.id === userId));
  return mine.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null;
}

/**
 * What to call `cal.currentWeek`: "this week" while today is inside its dates, "next
 * week" when it is still ahead — before the term starts and in the Christmas break,
 * `currentWeek` is the next week with tutorials, not the week it is.
 */
export function currentWeekIsNow(cal: Pick<TermView, 'slots' | 'currentWeek' | 'today'>): boolean {
  if (cal.currentWeek === null) return false;
  const slot = cal.slots.find((s) => s.week === cal.currentWeek);
  return Boolean(slot?.from && cal.today && slot.from <= cal.today && cal.today <= slot.to);
}

/**
 * A term without a calendar, as the chips need one: weeks W0 … W14 without dates and no
 * current week. Home uses it when nobody configured the term, so the weeks still work.
 */
export function undatedTermView(year: string, weeks = DEFAULT_TERM_WEEKS): TermView {
  return {
    calendar: { year, start: '', weeks, skipped: [], holidays: [] },
    source: 'course',
    updatedAt: null,
    updatedBy: null,
    slots: Array.from({ length: weeks }, (_, w) => ({ slot: w, week: w, from: '', to: '', skipped: false, holidays: [] })),
    currentWeek: null,
    today: '',
    courseDefault: null,
  };
}
